import "lib/@hooks/useEvent"
import { useEffect, useMemo, useRef, useState } from "react"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { CancelAsync, useIdleAsync } from "lib/@hooks/useIdleAsync"
import { cancelIdle, scheduleIdle } from "lib/schedule-idle-callback"
import {
    buildAuthoringRecommendationDisplayIndex,
    patchAuthoringRecommendationDisplayItems,
} from "../lib/authoring-recommendation-edit-scopes"
import { buildAuthoringRecommendationDisplayItemsIdle } from "../lib/build-authoring-recommendation-display-items-idle"
import { DraftEdited, DraftStructureChanged } from "../schedule-editor-event-definitions.mjs"
import { useScheduleEditorIds } from "./use-schedule-editor-ids"
import { useScheduleEditorDraftStructureVersion } from "./use-schedule-editor-draft-structure-version"

/**
 * Two-tier recommendation display list:
 * - Tier 1: idle full rebuild on structure changes / recommendations fetch
 * - Tier 2: scoped idle patch on field edits (conflict state only)
 *
 * @param {object} params
 * @param {object|null} params.draft
 * @param {object|symbol} params.recommendations
 * @param {string} params.sourceFilter
 * @param {object} params.selectedRecommendationIdsByGroupKey
 */
export function useAuthoringRecommendationDisplayList({
    draft,
    recommendations,
    sourceFilter,
    selectedRecommendationIdsByGroupKey,
}) {
    const { draftId } = useScheduleEditorIds()
    const draftStructureVersion = useScheduleEditorDraftStructureVersion()
    const [displayList, setDisplayList] = useState([])
    const indexRef = useRef(null)
    const pendingScopesRef = useRef(new Set())
    const idleHandleRef = useRef(null)

    const rebuildKey = useMemo(buildRebuildKey, [
        draftStructureVersion,
        recommendations,
        sourceFilter,
        selectedRecommendationIdsByGroupKey,
    ])
    const rebuildKeyId = useMemo(() => JSON.stringify(rebuildKey), [rebuildKey])

    const idleList = useIdleAsync(buildIdleList, [], rebuildKeyId)

    useEffect(cancelPendingEditPatchOnRebuild, [rebuildKeyId])
    useEffect(syncDisplayListFromIdleList, [idleList, rebuildKeyId])
    DraftEdited(draftId).useEvent(handleDraftEdited)
    DraftStructureChanged(draftId).useEvent(cancelPendingEditPatch)

    return displayList

    function buildRebuildKey() {
        return {
            draftStructureVersion,
            recommendationsKey: buildRecommendationsKey(recommendations),
            sourceFilter,
            selectionKey: JSON.stringify(selectedRecommendationIdsByGroupKey ?? {}),
        }
    }

    async function buildIdleList(yieldIdle) {
        if (recommendations === IS_LOADING) return CancelAsync

        return buildAuthoringRecommendationDisplayItemsIdle({
            recommendations: recommendations ?? [],
            draft,
            sourceFilter,
            selectedRecommendationIdsByGroupKey,
            yieldIdle,
        })
    }

    function syncDisplayListFromIdleList() {
        if (recommendations === IS_LOADING) return

        cancelPendingEditPatch()
        pendingScopesRef.current.clear()
        indexRef.current = buildAuthoringRecommendationDisplayIndex(recommendations ?? [], idleList)
        setDisplayList(idleList)
    }

    function cancelPendingEditPatchOnRebuild() {
        cancelPendingEditPatch()
    }

    function handleDraftEdited(payload) {
        const scopes = Array.isArray(payload?.scopes) ? payload.scopes : []
        if (!scopes.length) return

        for (const scope of scopes) pendingScopesRef.current.add(scope)
        queueIdlePatch()
    }

    function queueIdlePatch() {
        cancelIdle(idleHandleRef.current)
        idleHandleRef.current = scheduleIdle(runIdlePatch)
    }

    function runIdlePatch() {
        idleHandleRef.current = null

        const scopes = [...pendingScopesRef.current]
        pendingScopesRef.current.clear()

        if (!scopes.length || !indexRef.current || !draft) return

        setDisplayList((current) =>
            patchAuthoringRecommendationDisplayItems({
                items: current,
                draft,
                index: indexRef.current,
                scopes,
                selectedRecommendationIdsByGroupKey,
            })
        )
    }

    function cancelPendingEditPatch() {
        cancelIdle(idleHandleRef.current)
        idleHandleRef.current = null
        pendingScopesRef.current.clear()
    }
}

function buildRecommendationsKey(recommendations) {
    if (recommendations === IS_LOADING) return "loading"
    if (!Array.isArray(recommendations)) return "empty"
    return recommendations
        .map((item) => item?.id)
        .filter(Boolean)
        .join("|")
}
